/**
 * ItemSearchModal — Popup modal for searching and selecting inventory items.
 * Shows a table of matching products with columns: Item No., Item Name, Packing, Brand, CS, PC.
 * Clicking a row selects the item and closes the modal.
 */
import { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import {
  hteTransferService,
  InventorySearchItem,
} from '../../../services/hteTransferService';

interface ItemSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: InventorySearchItem) => void;
  initialQuery?: string;
}

const ItemSearchModal = ({ isOpen, onClose, onSelect, initialQuery = '' }: ItemSearchModalProps) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<InventorySearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset query and results when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery);
      setResults([]);
      // Focus the search input after mount
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, initialQuery]);

  // Debounced search — triggers 300ms after user stops typing
  useEffect(() => {
    if (!isOpen) return;
    if (query.length < 1) {
      setResults([]);
      return;
    }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await hteTransferService.searchItems(query);
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [query, isOpen]);

  // Handle row click — select item and close
  const handleSelect = (item: InventorySearchItem) => {
    onSelect(item);
    onClose();
  };

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
            {t('warehouseTransfer.searchItems')}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder={t('warehouseTransfer.searchItemPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') onClose();
              }}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Results table */}
        <div className="flex-1 overflow-y-auto">
          {searching && (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('common.loading')}
            </div>
          )}

          {!searching && query.length > 0 && results.length === 0 && (
            <div className="p-4 text-center text-sm text-gray-400 dark:text-gray-500">
              {t('warehouseTransfer.noSearchResults')}
            </div>
          )}

          {results.length > 0 && (
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 sticky top-0">
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('warehouseTransfer.itemNumber')}
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('warehouseTransfer.itemName')}
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('warehouseTransfer.packing')}
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('warehouseTransfer.brand')}
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('warehouseTransfer.cs')}
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('warehouseTransfer.pc')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.map((item, idx) => (
                  <tr
                    key={item.item_number}
                    onClick={() => handleSelect(item)}
                    className={`
                      cursor-pointer border-b border-gray-100 dark:border-gray-700/50
                      ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50'}
                      hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors
                    `}
                  >
                    <td className="px-3 py-2.5 text-sm font-mono font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {item.item_number}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100">
                      {item.item_chinese_name && (
                        <span className="text-gray-500 dark:text-gray-400">{item.item_chinese_name} </span>
                      )}
                      {item.item_english_name || '-'}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400">
                      {item.packing || '-'}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400">
                      {item.chinese_brand_name || item.english_brand_name || '-'}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right text-gray-400 dark:text-gray-500">
                      -
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right text-gray-400 dark:text-gray-500">
                      -
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemSearchModal;
